import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { RoomItem } from './RoomItem.jsx'
import { chatApi } from '../chat-api.js'
import { new_chat_room_path } from '../../../routes'
import { t } from '../chat-i18n.js'

const DEBOUNCE_MS = 400

/**
 * RoomList — sidebar room list with local name filter + debounced API search.
 *
 * Props: { store, cable }
 */
export function RoomList({ store, cable }) {
  const searchText    = useSignal('')
  const searchResults = useSignal(null) // null = use store.rooms; array = API results
  const debounceTimer = useRef(null)

  const rooms        = store.rooms.value
  const activeRoomId = store.activeRoomId.value
  const roomsLoading = store.roomsLoading.value

  // Immediate local filter + debounced API search
  useEffect(() => {
    const q = searchText.value.trim()

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    if (!q) {
      searchResults.value = null
      return
    }

    debounceTimer.current = setTimeout(() => {
      chatApi.search.rooms(q).then(async res => {
        if (res.ok) {
          const data = await res.json
          searchResults.value = Array.isArray(data) ? data : (data.rooms ?? [])
        }
      }).catch(() => { /* ignore */ })
    }, DEBOUNCE_MS)
  }, [searchText.value]) // eslint-disable-line

  // Local filter by name when there is no API result yet
  const q = searchText.value.trim().toLowerCase()
  const displayRooms = searchResults.value != null
    ? searchResults.value
    : q
      ? rooms.filter(r => (r.name || r.display_name || '').toLowerCase().includes(q))
      : rooms

  function handleRoomClick(room) {
    if (typeof store.navigateToRoom === 'function') {
      store.navigateToRoom(room.id)
    } else {
      store.activeRoomId.value = room.id
    }
  }

  return (
    <div class="chat-sidebar-inner">
      {/* Sidebar header */}
      <div class="chat-sidebar-header">
        <span class="chat-sidebar-title">{t('chat.title')}</span>
        <div class="chat-sidebar-header-actions">
          {store.canManageRooms && (
            <>
              <a href={new_chat_room_path({ type: 'direct' })}
                 class="chat-sidebar-action-btn"
                 title={t('chat.new_direct_title')}
                 data-turbo-frame="modal">
                <i class="bi bi-person-plus"></i>
              </a>
              <a href={new_chat_room_path({ type: 'group' })}
                 class="chat-sidebar-action-btn"
                 title={t('chat.new_group_title')}
                 data-turbo-frame="modal">
                <i class="bi bi-people"></i>
              </a>
            </>
          )}
        </div>
      </div>

      {/* Search input */}
      <div class="chat-sidebar-search">
        <i class="bi bi-search chat-sidebar-search-icon"></i>
        <input
          type="search"
          class="chat-sidebar-search-input"
          placeholder={t('chat.room_search_placeholder')}
          value={searchText.value}
          onInput={e => { searchText.value = e.target.value }}
        />
        {searchText.value && (
          <button
            type="button"
            class="chat-sidebar-search-clear"
            onClick={() => { searchText.value = ''; searchResults.value = null }}
          >
            <i class="bi bi-x"></i>
          </button>
        )}
      </div>

      {/* Room list */}
      <div class="chat-sidebar-rooms">
        {roomsLoading && rooms.length === 0 ? (
          <div class="chat-rooms-loader">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : displayRooms.length === 0 && q ? (
          <div class="chat-sidebar-empty">{t('chat.search_no_results')}</div>
        ) : (
          displayRooms.map(room => (
            <RoomItem
              key={room.id}
              room={room}
              store={store}
              cable={cable}
              active={room.id === activeRoomId}
              onClick={() => handleRoomClick(room)}
            />
          ))
        )}
      </div>
    </div>
  )
}
